import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { sendOrderEmail } from "@/lib/email";
import { ShoppingCart, Phone, Mail, MessageCircle } from "lucide-react";

interface OrderDialogProps {
  artworkTitle: string;
  artworkId: string;
  artworkPrice: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const OrderDialog = ({ artworkTitle, artworkId, artworkPrice, open: externalOpen, onOpenChange }: OrderDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const { toast } = useToast();

  const isExternallyControlled = externalOpen !== undefined;
  const open = isExternallyControlled ? externalOpen : internalOpen;
  const setOpen = (newOpen: boolean) => {
    if (isExternallyControlled && onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Валидация данных перед отправкой
    if (formData.name.trim().length < 2) {
      toast({
        title: "Ошибка валидации",
        description: "Имя должно содержать минимум 2 символа",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    if (formData.name.trim().length > 100) {
      toast({
        title: "Ошибка валидации",
        description: "Имя не должно превышать 100 символов",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast({
        title: "Ошибка валидации",
        description: "Пожалуйста, введите корректный email",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    try {
      const orderMessage = `Заказ картины: "${artworkTitle}" (ID: ${artworkId})
Цена: ${artworkPrice.toLocaleString('ru-RU')} ₽

Сообщение: ${formData.message}`;

      // Добавляем ссылку на товар в заказ
      const artworkUrl = `${window.location.origin}/gallery/${artworkId}`;
      
      // Отправляем заказ (email + VK в одном запросе)
      const result = await sendOrderEmail({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim(),
        artworkTitle,
        artworkId,
        artworkPrice,
        artworkUrl
      });

      if (result) {
        toast({
          title: "Заказ принят!",
          description: "Спасибо за ваш заказ! Мы свяжемся с вами для уточнения деталей.",
        });
        setOpen(false);
        setFormData({ name: "", email: "", phone: "", message: "" });
      } else {
        throw new Error("Ошибка обработки заказа");
      }
    } catch (error) {
      // Специальная обработка rate limit ошибки
      if (error.message.startsWith('RATE_LIMIT_EXCEEDED')) {
        const waitMinutes = error.message.split(':')[1] || '2';
        toast({
          title: "Превышен лимит запросов",
          description: `Слишком много попыток оформить заказ. Повторите через ${waitMinutes} мин.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Ошибка заказа",
          description: "Произошла ошибка при оформлении заказа. Попробуйте еще раз или свяжитесь с нами напрямую.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Обработчик только для сброса формы при закрытии
  const handleDialogOpenChange = (newOpen: boolean) => {
    if (isExternallyControlled && onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
    if (!newOpen) {
      // Сбрасываем форму только при закрытии
      setFormData({ name: "", email: "", phone: "", message: "" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      {/* Показываем триггер только если нет внешнего управления */}
      {!isExternallyControlled && (
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 font-medium"
          >
            <ShoppingCart className="w-3 h-3 mr-1" />
            Заказать
          </Button>
        </DialogTrigger>
      )}
      
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Заказ картины
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            "{artworkTitle}" - {artworkPrice.toLocaleString('ru-RU')} ₽
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Имя *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              placeholder="Ваше имя"
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
                placeholder="your@email.com"
                className="pl-10"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="phone">Телефон</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+7 (999) 123-45-67"
                className="pl-10"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="message">Сообщение</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Дополнительные пожелания или вопросы..."
              rows={3}
            />
          </div>

          <div className="pt-6 space-y-4 border-t border-border/50">
            <div className="text-center">
              <p className="text-sm font-medium text-muted-foreground mb-3">
                Альтернативные способы связи
              </p>
              
              <div className="grid grid-cols-2 gap-2 mb-3">
                <a
                  href="tel:+79088005799"
                  className="flex items-center justify-center gap-2 p-3 rounded-lg border border-border hover:bg-accent hover:text-accent-foreground transition-all duration-200 text-sm"
                >
                  <Phone className="w-4 h-4" />
                  <div className="text-left">
                    <div className="font-medium">Телефон</div>
                    <div className="text-xs text-muted-foreground">8-908-800-57-99</div>
                  </div>
                </a>
                
                <a
                  href="mailto:info@w-wagner.art"
                  className="flex items-center justify-center gap-2 p-3 rounded-lg border border-border hover:bg-accent hover:text-accent-foreground transition-all duration-200 text-sm"
                >
                  <Mail className="w-4 h-4" />
                  <div className="text-left">
                    <div className="font-medium">Email</div>
                    <div className="text-xs text-muted-foreground">info@w-wagner.art</div>
                  </div>
                </a>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <a
                  href="https://wa.me/79088005799"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 p-3 rounded-lg border border-border hover:bg-accent hover:text-accent-foreground transition-all duration-200 text-sm"
                >
                  <MessageCircle className="w-4 h-4" />
                  <div className="text-center">
                    <div className="font-medium">WhatsApp</div>
                    <div className="text-xs text-muted-foreground">Быстрый ответ</div>
                  </div>
                </a>
                
                <a
                  href="https://vk.com/im?sel=-214021218"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 p-3 rounded-lg border border-border hover:bg-accent hover:text-accent-foreground transition-all duration-200 text-sm"
                >
                  <MessageCircle className="w-4 h-4" />
                  <div className="text-center">
                    <div className="font-medium">ВКонтакте</div>
                    <div className="text-xs text-muted-foreground">Написать</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button 
              type="submit" 
              disabled={loading}
              className="flex-1"
            >
              {loading ? "Отправляем..." : "Отправить заказ"}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setOpen(false);
                setFormData({ name: "", email: "", phone: "", message: "" });
              }}
            >
              Отмена
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDialog;